import { useEffect, useState } from "preact/hooks";
import { locale, tr } from "../i18n.js";
import { del, get, html, LOST_REASON_LABEL, post, put, stageLabel, STAGES } from "../lib.js";
import { Badge, Field, Modal, toast, useAction, useLoad } from "../ui.js";
import { LINK_SOURCE_LABEL } from "./links.js";

/**
 * Saved segments: named audience rules that stay current ("haven't visited in 90 days"). Each
 * shows everyone who matches and how many a campaign may reach (WhatsApp, opted in), so the gap is
 * visible. Rules match `Audience` in src/campaigns/service.ts.
 */

const NPS_BANDS = { promoter: "Promoters (9-10)", passive: "Passives (7-8)", detractor: "Unhappy (0-6)" };
const toggle = (list = [], v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

/** Drops empty rules, so a saved segment carries only what was actually set. */
export function cleanAudience(a) {
  const out = { stages: a.stages ?? [] };
  for (const k of ["active_within_days", "no_visit_within_days", "min_visits"]) if (Number(a[k]) > 0) out[k] = Number(a[k]);
  if (a.no_upcoming) out.no_upcoming = true;
  for (const k of ["service_ids", "lost_reasons", "nps_bands", "sources"]) if (a[k]?.length) out[k] = a[k];
  return out;
}

/** One line a person can read: "Lost · reason Price · no visit in 90 days". */
export function describeAudience(a, services = []) {
  const parts = [];
  if (a.stages?.length) parts.push(a.stages.map(stageLabel).join(", "));
  if (a.no_visit_within_days) parts.push(tr("no visit in {n} days", { n: a.no_visit_within_days }));
  if (a.no_upcoming) parts.push(tr("nothing booked ahead"));
  if (a.min_visits) parts.push(tr("{n}+ visits", { n: a.min_visits }));
  if (a.service_ids?.length) parts.push(a.service_ids.map((id) => services.find((s) => s.id === id)?.name ?? id).join(", "));
  if (a.lost_reasons?.length) parts.push(a.lost_reasons.map((r) => tr(LOST_REASON_LABEL[r] ?? r)).join(", "));
  if (a.nps_bands?.length) parts.push(a.nps_bands.map((b) => tr(NPS_BANDS[b])).join(", "));
  if (a.sources?.length) parts.push(a.sources.map((s) => (LINK_SOURCE_LABEL[s] ? tr(LINK_SOURCE_LABEL[s]) : s)).join(", "));
  if (a.active_within_days) parts.push(tr("wrote in the last {n} days", { n: a.active_within_days }));
  return parts.join(" · ") || tr("Everyone");
}

/** Segments the starter pack or a preset created are stored under their English name; show it in the reader's language. */
const segmentName = (name) => tr(name);

export function SegmentsCard({ t, onUse }) {
  const [data, reload] = useLoad(() => get(`${t.base}/segments`), [t.id]);
  const [services] = useLoad(() => get(`${t.base}/services`), [t.id]);
  const [editing, setEditing] = useState(null);
  const [busy, run] = useAction();
  if (!data) return null;
  const canEdit = t.role !== "agent";
  const unusedPresets = data.presets.filter((p) => !data.segments.some((s) => s.name === p.name || s.name === tr(p.name)));
  const remove = async (s) => {
    if (!confirm(tr("Delete the segment \"{name}\"? Campaigns already made from it are not affected.", { name: s.name }))) return;
    await run(() => del(`${t.base}/segments/${s.id}`), tr("Segment deleted"));
    reload();
  };
  return html`<section class="card">
    <div class="section-head"><h2>${tr("Segments")}</h2>${canEdit && html`<button class="btn small" onClick=${() => setEditing({ audience: { stages: [] } })}>${tr("New segment")}</button>`}</div>
    <p class="muted small">${tr("Groups of customers that update themselves, such as people who haven't visited in 90 days. Use one as a campaign audience; only contacts who agreed to receive offers on WhatsApp are messaged.")}</p>
    ${data.segments.length > 0 && html`<table class="table"><tbody>${data.segments.map((s) => html`<tr>
      <td><strong dir="auto">${segmentName(s.name)}</strong><div class="muted small">${describeAudience(s.audience, services?.services)}</div></td>
      <td class="small"><strong>${s.matching}</strong> ${tr("match")} · <strong>${s.reachable}</strong> ${tr("can receive campaigns")}</td>
      <td class="row-actions">
        ${canEdit && onUse && html`<button class="btn small primary" disabled=${!s.reachable} onClick=${() => onUse(s)}>${tr("Campaign")}</button>`}
        ${canEdit && html`<button class="btn small ghost" onClick=${() => setEditing(s)}>${tr("Edit")}</button><button class="btn small ghost" disabled=${busy} onClick=${() => remove(s)}>${tr("Delete")}</button>`}
      </td></tr>`)}</tbody></table>`}
    ${canEdit && unusedPresets.length > 0 && html`<div class="chips"><span class="muted small">${tr("Start from:")}</span>${unusedPresets.map((p) => html`<button type="button" class="chip" onClick=${() => setEditing({ name: tr(p.name), audience: p.audience })}>+ ${tr(p.name)}</button>`)}</div>`}
    ${editing && html`<${SegmentEditor} t=${t} segment=${editing} services=${services?.services ?? []} onClose=${() => setEditing(null)} onSaved=${() => { setEditing(null); reload(); }} />`}
  </section>`;
}

function SegmentEditor({ t, segment, services, onClose, onSaved }) {
  const [name, setName] = useState(segment.name ?? "");
  const [a, setA] = useState({ stages: [], ...segment.audience });
  const [preview, setPreview] = useState(null);
  const [busy, run] = useAction();
  const set = (k, v) => setA((x) => ({ ...x, [k]: v }));
  const audience = cleanAudience(a);
  useEffect(() => {
    const id = setTimeout(() => post(`${t.base}/segments/preview`, audience).then(setPreview).catch(() => setPreview(null)), 250);
    return () => clearTimeout(id);
  }, [JSON.stringify(audience)]);
  const save = async (e) => {
    e.preventDefault();
    await run(() => (segment.id ? put(`${t.base}/segments/${segment.id}`, { name, audience }) : post(`${t.base}/segments`, { name, audience })), tr("Segment saved"));
    onSaved();
  };
  const num = (k, label, hint) => html`<${Field} label=${label} hint=${hint}><input type="number" min="1" value=${a[k] ?? ""} onInput=${(e) => set(k, e.target.value)} /></${Field}>`;
  return html`<${Modal} title=${segment.id ? tr("Edit segment") : tr("New segment")} onClose=${onClose} wide>
    <form onSubmit=${save}>
      <${Field} label=${tr("Name")}><input required maxlength="80" dir="auto" value=${name} onInput=${(e) => setName(e.target.value)} /></${Field}>
      <h3>${tr("Stage")}</h3>
      <div class="chips">${STAGES.map((s) => html`<button type="button" class=${a.stages?.includes(s) ? "chip active" : "chip"} onClick=${() => set("stages", toggle(a.stages, s))}>${stageLabel(s)}</button>`)}</div>
      <div class="row">
        ${num("no_visit_within_days", tr("No visit in the last (days)"), tr("Customers who came at least once, but not lately"))}
        ${num("min_visits", tr("At least this many visits"))}
        ${num("active_within_days", tr("Wrote in the last (days)"))}
      </div>
      <label class="toggle"><input type="checkbox" checked=${!!a.no_upcoming} onChange=${(e) => set("no_upcoming", e.target.checked)} /> ${tr("Nothing booked ahead")}</label>
      ${services.length > 0 && html`<h3>${tr("Had one of these services")}</h3>
        <div class="chips">${services.map((s) => html`<button type="button" class=${a.service_ids?.includes(s.id) ? "chip active" : "chip"} onClick=${() => set("service_ids", toggle(a.service_ids, s.id))}>${s.name}</button>`)}</div>`}
      <h3>${tr("Lost because")}</h3>
      <div class="chips">${Object.entries(LOST_REASON_LABEL).map(([k, v]) => html`<button type="button" class=${a.lost_reasons?.includes(k) ? "chip active" : "chip"} onClick=${() => set("lost_reasons", toggle(a.lost_reasons, k))}>${tr(v)}</button>`)}</div>
      <h3>${tr("Recommend score")}</h3>
      <div class="chips">${Object.entries(NPS_BANDS).map(([k, v]) => html`<button type="button" class=${a.nps_bands?.includes(k) ? "chip active" : "chip"} onClick=${() => set("nps_bands", toggle(a.nps_bands, k))}>${tr(v)}</button>`)}</div>
      <${Field} label=${tr("First came from")} hint=${tr("Lead sources, comma-separated, e.g. meta_ads, google, qr_code. Leave empty for any.")}>
        <input dir="ltr" value=${(a.sources ?? []).join(", ")} onInput=${(e) => set("sources", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} /></${Field}>
      <div class="callout small">
        ${preview
          ? html`<strong>${preview.matching}</strong> ${tr("match")} · <strong>${preview.reachable}</strong> ${tr("can receive campaigns")}
            ${preview.members.length > 0 && html`<ul class="plain segment-members">${preview.members.slice(0, 12).map((m) => html`<li>
              <span dir="auto">${m.name ?? m.phone ?? "—"}</span> <span class="muted">${stageLabel(m.stage)}${m.last_visit ? ` · ${tr("last visit {date}", { date: new Date(m.last_visit).toLocaleDateString(locale()) })}` : ""}</span>
              ${!m.reachable && html` <${Badge} kind="muted">${tr("no consent")}</${Badge}>`}</li>`)}</ul>`}`
          : tr("Counting…")}
      </div>
      <div class="form-actions"><button class="btn primary" disabled=${busy}>${tr("Save segment")}</button><button type="button" class="btn ghost" onClick=${onClose}>${tr("Cancel")}</button></div>
    </form>
  </${Modal}>`;
}
